import * as t from "io-ts";
import { tierCodec } from "@glide/plugins-codecs";
import { isLeft } from "fp-ts/lib/Either";
import { scheduleKindCodec, automationTriggerKind, automationMaxStepsCodec } from "@glide/automations-codecs";

// NOTE: These types are serialized and must not be changed in a
// backward-incompatible way!

/**
 * Definitions
 *
 * EminenceFlags   - The type of a single instance of an entitlement. Name is a legacy reference to the old codebase
 *                   and is not changed in order to minimize migration. Should be Entitlements.
 *
 * Entitlements    - An object containing an EminenceFlags, an id, and a timestamp.
 *
 * SkuEntitlementsHistory - An object representing the entitlements history of a SKU.
 */

export const PluginAccessOverride = {
    Allow: "allow",
    Deny: "deny",
    NoOverride: "no-override",
} as const;
export type PluginAccessOverride = (typeof PluginAccessOverride)[keyof typeof PluginAccessOverride];

const quotaCodec = t.exact(
    t.type({
        prepaidUsage: t.number,
        maxOverage: t.number,
    })
);

export type Quota = t.TypeOf<typeof quotaCodec>;

export const eminenceFlagQuotasCodec = t.exact(
    t.intersection([
        t.type({
            "airtable-rows-used": quotaCodec,
            "app-editors": quotaCodec,
            "barcodes-scanned": quotaCodec,
            "big-table-rows": quotaCodec,
            "deliver-email": quotaCodec,
            "file-bytes-used": quotaCodec,
            geocodes: quotaCodec,
            "map-pins": quotaCodec,
            "private-users": quotaCodec,
            "public-users": quotaCodec,
            "published-apps": quotaCodec,
            reloads: quotaCodec,
            "rows-used": quotaCodec,
            signatures: quotaCodec,
            "sql-rows": quotaCodec,
            updates: quotaCodec,
            users: quotaCodec, // unified public/private
            zaps: quotaCodec,
            "glide-agent": quotaCodec,
        }),
        // New fields should be added in the schema below to ensure backwards compatibility during deployments, where
        // the order of the deploy is not stable.
        t.partial({
            "glide-agent-tool-calls": quotaCodec,
        }),
    ])
);

export type EminenceFlagQuotas = t.TypeOf<typeof eminenceFlagQuotasCodec>;

const booleanEminenceFlagsCodec = t.intersection([
    t.type({
        appLoginsSheet: t.boolean,
        appUserRequestAccessInPrivateProjectsDefault: t.boolean,
        automaticRefresh: t.boolean,
        base64EncodePublicUserProfiles: t.boolean,
        bigQuery: t.boolean,
        bigTables: t.boolean,
        canAccessAnalytics: t.boolean,
        canChangeAuthType: t.boolean,
        canCreateSupportTicket: t.boolean,
        canCustomizeSignIn: t.boolean,
        canDisableShare: t.boolean,
        canDowngrade: t.boolean,
        canPausePublish: t.boolean,
        canSubmitTemplate: t.boolean,
        canTransferBetweenUsers: t.boolean,
        canTransferToOrg: t.boolean,
        canUpgrade: t.boolean,
        canUseSignInWithGoogle: t.boolean,
        confirmChangeAuthOnUpgrade: t.boolean,
        customDomains: t.boolean,
        customSignInAppearance: t.boolean,
        downgradeBeforeDelete: t.boolean,
        gmailForSendEmails: t.boolean,
        googleAnalytics: t.boolean,
        googleSharedDrive: t.boolean,
        integrationsCountTowardUpdatesQuota: t.boolean,
        isAgency: t.boolean,
        isFreeEminence: t.boolean,
        isPrivateProTrial: t.boolean,
        isUserMetered: t.boolean,
        isV1Pro: t.boolean,
        logOrgLogin: t.boolean,
        needsBuilderModifyAppFeatures: t.boolean,
        needsPublishableOrgToTransfer: t.boolean,
        newNewAppFlow: t.boolean,
        offlineActionQueue: t.boolean,
        pagesCustomCss: t.boolean,
        personalUsersOnly: t.boolean,
        proComponents: t.boolean,
        proDisplayQuotas: t.boolean,
        proIcons: t.boolean,
        protectedColumns: t.boolean,
        removeBranding: t.boolean,
        roles: t.boolean,
        rowQuotasIncludeBigTables: t.boolean,
        showQuotaBanners: t.boolean,
        // This is not used anymore
        showReportApp: t.boolean,
        shouldUseNewPrivacy: t.boolean,
        signInAgreements: t.boolean,
        supportsQuotaUpgrades: t.boolean,
        tabletMode: t.boolean,
        unlimitedPasswordAndTeamMembersOnlyUsers: t.boolean,
        unlimitedZapier: t.boolean,
        upgradeButton: t.boolean,
        virtualEmailAddressesInPublicProjectsDefault: t.boolean,
        withGrandfathering: t.boolean,
        queryTablesAPICountsTowardUpdatesQuota: t.boolean,
        mutateTablesAPICountsTowardUpdatesQuota: t.boolean,
        signaturesCountTowardUpdatesQuota: t.boolean,
        userCRUDActionsOnInternalDataSourcesCountTowardUpdatesQuota: t.boolean,
        userCRUDActionsOnExternalDataSourcesCountTowardUpdatesQuota: t.boolean,
        glideAPIv2CountsTowardUpdatesQuota: t.boolean,
        // Can the org be deleted by the org's admin, or does it require support?
        // https://github.com/glideapps/glide/issues/24912
        deletingOrgRequiresSupport: t.boolean,
        // Count PgMirror syncs (Airtable, Excel) as updates?
        pgMirrorSyncsCountTowardUpdatesQuota: t.boolean,
        // Prevents updates usage under any circumstances.
        blockUpdatesUsage: t.boolean,
        // Prevents users usage under any circumstances.
        blockUsersUsage: t.boolean,
        glideAgentUsageCountsTowardUpdatesQuota: t.boolean,
    }),
    // New fields should be added in the schema below to ensure backwards compatibility during deployments, where
    // the order of the deploy is not stable.
    t.partial({
        automaticLogout: t.boolean,
        canUseBackups: t.boolean,
        disallowCloudinary: t.boolean, // negative because default is false
        folderPermissions: t.boolean,
        printScreenToPDF: t.boolean
    }),
]);

type BooleanEminenceFlags = t.TypeOf<typeof booleanEminenceFlagsCodec>;

type BooleanEminenceFlagsKeys = keyof BooleanEminenceFlags;

export const booleanEminenceFlagsKeys: BooleanEminenceFlagsKeys[] = booleanEminenceFlagsCodec.types.flatMap(
    ty => Object.keys(ty.props) as (keyof typeof ty.props)[]
);

export const eminenceFlagsCodec = t.intersection([
    t.exact(
        t.type({
            allowedPlugins: t.array(t.string),
            allowRealEmails: t.union([t.literal("private"), t.literal("public"), t.boolean]),
            apiCapabilities: t.exact(
                t.type({
                    mutateTables: t.boolean,
                    queryTables: t.boolean,
                    requestReload: t.boolean,
                    createAnonymousUser: t.boolean,
                })
            ),
            badge: t.union([t.string, t.undefined]),
            canAppUserRequestAccess: t.union([t.literal("private"), t.literal("public"), t.boolean]),
            displayName: t.string,
            downgradeBeforeDeleteStripeCancellation: t.union([t.boolean, t.undefined, t.literal("immediately")]),
            includedWhitelabels: t.number,
            maxSingleUploadBytes: t.number,
            pluginTier: tierCodec,
            quotaContext: t.union([t.literal("app"), t.literal("org")]),
            quotaEnforcementScheme: t.union([t.literal("free"), t.literal("paid"), t.undefined]),
            stripeTransactionFeePercentage: t.number,
            transferToOrgAs: t.union([t.literal("internal"), t.literal("public"), t.literal("free"), t.undefined]),
            authenticationSupport: t.exact(
                t.type({
                    public: t.union([t.boolean, t.literal("upgrade")]),
                    publicWithEmail: t.union([t.boolean, t.literal("upgrade")]),
                    publicDisabled: t.union([t.boolean, t.literal("upgrade")]),
                    password: t.union([t.boolean, t.literal("upgrade")]),
                    emailWhitelist: t.union([t.boolean, t.literal("upgrade")]),
                    userProfiles: t.union([t.boolean, t.literal("upgrade")]),
                    allowedEmailDomains: t.union([t.boolean, t.literal("upgrade")]),
                    organizationMembers: t.union([t.boolean, t.literal("upgrade")]),
                    justMe: t.union([t.boolean, t.literal("upgrade")]),
                    optionalPublicWithEmail: t.union([t.boolean, t.literal("upgrade")]),
                    optionalOtherEmail: t.union([t.boolean, t.literal("upgrade")]),
                })
            ),
            pluginAllowList: t.array(t.string),
            pluginDenyList: t.array(t.string),
            quotas: eminenceFlagQuotasCodec,
            actionLogsHistoryLength: t.number,
            automationTriggerScheduleBillableConsumed: t.number,
            automationScheduleKind: t.array(scheduleKindCodec),
            automationTriggerKind: t.array(automationTriggerKind),
            automationMaxStepsLimit: automationMaxStepsCodec,
            // DO NOT ADD NEW ENTITLEMENTS HERE - see comment below
        })
    ),
    t.exact(
        t.partial({
            // *************************************************************
            // New entitlements go below this line to ensure backwards compatibility:
            // *************************************************************
            glideAgentPricingScheme: t.union([t.literal("message-only"), t.literal("message-tools-and-tokens")]),
            automationMaxConcurrentRuns: t.number,
            // NOTE: Except for `max` these must all be parsable by `ms`.
            backupFrequency: t.union([t.literal("max"), t.literal("1h"), t.literal("1d"), t.literal("1w")]),
            backupRetention: t.union([
                t.literal("max"),
                t.literal("1y"),
                t.literal("24w"),
                t.literal("12w"),
                t.literal("4w"),
                t.literal("1w"),
                t.literal("1d"),
            ]),
        })
    ),
    t.exact(booleanEminenceFlagsCodec),
]);

export type EminenceFlags = t.TypeOf<typeof eminenceFlagsCodec>;

export const defaultEminenceFlagsValues: EminenceFlags = {
    allowRealEmails: true,
    apiCapabilities: {
        mutateTables: false,
        queryTables: false,
        requestReload: false,
        createAnonymousUser: false,
    },
    appLoginsSheet: true,
    appUserRequestAccessInPrivateProjectsDefault: true,
    automaticRefresh: false,
    badge: undefined,
    base64EncodePublicUserProfiles: true,
    bigQuery: false,
    bigTables: false,
    canAccessAnalytics: false,
    canAppUserRequestAccess: false,
    canChangeAuthType: true,
    canCreateSupportTicket: false,
    canCustomizeSignIn: false,
    canDisableShare: false,
    canDowngrade: false,
    canPausePublish: false,
    canSubmitTemplate: true,
    canTransferBetweenUsers: false,
    canTransferToOrg: true,
    canUpgrade: false,
    canUseSignInWithGoogle: true,
    confirmChangeAuthOnUpgrade: true,
    customDomains: false,
    customSignInAppearance: false,
    displayName: "Free",
    downgradeBeforeDelete: true,
    downgradeBeforeDeleteStripeCancellation: undefined,
    gmailForSendEmails: false,
    googleAnalytics: false,
    googleSharedDrive: false,
    includedWhitelabels: 0,
    integrationsCountTowardUpdatesQuota: false,
    isAgency: false,
    isFreeEminence: false,
    isPrivateProTrial: false,
    isUserMetered: false,
    isV1Pro: false,
    logOrgLogin: false,
    maxSingleUploadBytes: 1024,
    needsBuilderModifyAppFeatures: false,
    needsPublishableOrgToTransfer: false,
    newNewAppFlow: false,
    offlineActionQueue: false,
    pagesCustomCss: false,
    personalUsersOnly: false,
    pluginTier: "free",
    proComponents: false,
    proDisplayQuotas: false,
    proIcons: false,
    protectedColumns: false,
    quotaContext: "app",
    quotaEnforcementScheme: undefined,
    removeBranding: false,
    roles: false,
    rowQuotasIncludeBigTables: false,
    shouldUseNewPrivacy: true,
    showQuotaBanners: true,
    showReportApp: true,
    signInAgreements: false,
    stripeTransactionFeePercentage: 5,
    supportsQuotaUpgrades: true,
    tabletMode: false,
    transferToOrgAs: "public",
    unlimitedPasswordAndTeamMembersOnlyUsers: false,
    unlimitedZapier: false,
    upgradeButton: true,
    virtualEmailAddressesInPublicProjectsDefault: false,
    withGrandfathering: false,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: false,
        emailWhitelist: false,
        userProfiles: false,
        allowedEmailDomains: true,
        organizationMembers: false,
        justMe: false,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    // With ##unifiedPluginEminenceChecks there is only one place where we
    // check these, at least for "real" plugins.
    pluginAllowList: [],
    pluginDenyList: [],
    allowedPlugins: [],
    quotas: {
        "airtable-rows-used": { prepaidUsage: 500, maxOverage: 0 },
        "app-editors": { prepaidUsage: 2, maxOverage: 0 },
        "barcodes-scanned": { prepaidUsage: 0, maxOverage: 0 },
        "big-table-rows": { prepaidUsage: 100_000, maxOverage: 0 },
        "deliver-email": { prepaidUsage: 0, maxOverage: 0 },
        "file-bytes-used": { prepaidUsage: 200 * 1024 * 1024, maxOverage: 0 },
        geocodes: { prepaidUsage: 10, maxOverage: 0 },
        "map-pins": { prepaidUsage: 10, maxOverage: 0 },
        "private-users": { prepaidUsage: 3, maxOverage: 0 },
        "public-users": { prepaidUsage: 10, maxOverage: 0 },
        "published-apps": { prepaidUsage: 3, maxOverage: 0 },
        reloads: { prepaidUsage: 1000, maxOverage: Number.MAX_SAFE_INTEGER },
        "rows-used": { prepaidUsage: 500, maxOverage: 0 },
        signatures: { prepaidUsage: 0, maxOverage: 0 },
        "sql-rows": { prepaidUsage: 100_000, maxOverage: 0 },
        updates: { prepaidUsage: 1000, maxOverage: 0 },
        users: { prepaidUsage: Number.MAX_SAFE_INTEGER, maxOverage: 0 },
        zaps: { prepaidUsage: Number.MAX_SAFE_INTEGER, maxOverage: 0 },
        "glide-agent": { prepaidUsage: Number.MAX_SAFE_INTEGER, maxOverage: 0 },
        "glide-agent-tool-calls": { prepaidUsage: Number.MAX_SAFE_INTEGER, maxOverage: 0 },
    },
    queryTablesAPICountsTowardUpdatesQuota: false,
    mutateTablesAPICountsTowardUpdatesQuota: true,
    glideAPIv2CountsTowardUpdatesQuota: true,
    signaturesCountTowardUpdatesQuota: false,
    userCRUDActionsOnInternalDataSourcesCountTowardUpdatesQuota: true,
    userCRUDActionsOnExternalDataSourcesCountTowardUpdatesQuota: true,
    deletingOrgRequiresSupport: false,
    actionLogsHistoryLength: 7,
    automationTriggerScheduleBillableConsumed: 1,
    automationScheduleKind: [],
    automationTriggerKind: ["app"],
    automationMaxStepsLimit: 2000,
    automationMaxConcurrentRuns: 10,
    pgMirrorSyncsCountTowardUpdatesQuota: false,
    blockUpdatesUsage: false,
    blockUsersUsage: false,
    glideAgentUsageCountsTowardUpdatesQuota: false,
    glideAgentPricingScheme: undefined,
};

export type Entitlements = {
    readonly id: string;
    readonly timestamp: Date;
    readonly flags: EminenceFlags;
    readonly shortName?: string;
    readonly comments?: string;
    readonly referenceUrl?: string;
    readonly kind: "fallback" | "legacy" | "defined" | "free-override";
};

export type SkuEntitlementsHistory = {
    readonly id: string;
    readonly name: string;
    readonly entitlements: readonly Entitlements[];
};

export type EntitlementsOverlay = {
    id: string;
    ownerId: string;
    timestamp: Date;
    overlay: object;
};

export interface EntitlementsClient {
    /**
     * This returns the base entitlements history for all SKUs
     */
    readonly getAllEntitlementsHistory: () => Promise<Record<string, SkuEntitlementsHistory>>;
    /**
     * This returns the modified entitlements for an owner based on the SKU entitlements and owner settings
     */
    readonly getEntitlementsForOwnerID: (ownerID: string, allowOverages?: boolean) => Promise<Entitlements>;
    /**
     * This returns the base entitlements for a SKU
     */
    readonly getEntitlementsForPriceID: (priceID: string) => Promise<Entitlements>;
    /**
     * This returns the base entitlements history for a SKU
     */
    readonly getEntitlementsHistoryForPriceID: (priceID: string) => Promise<SkuEntitlementsHistory>;
    readonly updateEntitlementsFieldsForId: (
        id: string,
        comments: string,
        referenceUrl: string,
        shortName: string
    ) => Promise<void>;
    readonly upsertEntitlementsForPriceID: (
        priceID: string,
        flags: EminenceFlags,
        comments?: string,
        referenceUrl?: string,
        shortName?: string
    ) => Promise<boolean>;

    /**
     * Returns the entitlements overlay for a given owner ID, or undefined if not found.
     */
    readonly getEntitlementsOverlayForOwnerID: (ownerID: string) => Promise<EntitlementsOverlay | undefined>;

    readonly getEntitlementsOverlaysHistoryForOwnerID: (ownerID: string) => Promise<EntitlementsOverlay[]>;

    readonly insertEntitlementsOverlayForOwnerID: (ownerID: string, timestamp: Date, overlay: object) => Promise<void>;

    readonly getAllOwnerIDsWithEntitlementsOverlays: () => Promise<string[]>;

    readonly getAllOwnerIDsWithSpecificEntitlements: (flags: Partial<EminenceFlags>) => Promise<string[]>;
}

/**
 * @returns The validated eminence flags object, or undefined if the input is invalid.
 */
export function validateEminenceFlags(unvalidatedEminenceFlags: unknown): EminenceFlags | undefined {
    const decodeResult = eminenceFlagsCodec.decode(unvalidatedEminenceFlags);
    if (isLeft(decodeResult)) {
        return undefined;
    }

    return decodeResult.right;
}

export function eminenceFlagsAreComplete(eminenceFlags: unknown): boolean {
    const validatedEminenceFlags = validateEminenceFlags(eminenceFlags);
    return validatedEminenceFlags !== undefined;
}
